import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { easeOutStrong } from "@/lib/motion";

// Reusable hero with eyebrow + title + body + optional CTAs + optional kicker chips
export function PageHero({
    eyebrow,
    title,
    subtitle,
    primaryCta,
    secondaryCta,
    children,
}) {
    return (
        <section className="bg-white border-b border-slate-100">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
                {eyebrow && (
                    <motion.div
                        className="label-eyebrow text-slate-500 mb-4"
                        data-testid="hero-eyebrow"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: easeOutStrong }}
                    >
                        {eyebrow}
                    </motion.div>
                )}
                <motion.h1
                    className="font-display font-black tracking-tight text-slate-950 text-4xl sm:text-5xl lg:text-6xl leading-[1.04] max-w-4xl"
                    data-testid="hero-title"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.1, ease: easeOutStrong }}
                >
                    {title}
                </motion.h1>
                {subtitle && (
                    <motion.p
                        className="text-base sm:text-lg text-slate-600 mt-6 max-w-2xl leading-relaxed"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3, ease: easeOutStrong }}
                    >
                        {subtitle}
                    </motion.p>
                )}
                {(primaryCta || secondaryCta) && (
                    <motion.div
                        className="mt-10 flex flex-wrap gap-3"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.45, ease: easeOutStrong }}
                    >
                        {primaryCta && (
                            <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }} transition={{ duration: 0.2 }}>
                                <Button
                                    asChild
                                    size="lg"
                                    className="bg-slate-950 hover:bg-slate-800 text-white font-semibold"
                                    data-testid="hero-primary-cta"
                                >
                                    <Link to={primaryCta.to}>
                                        {primaryCta.label}
                                        <ArrowUpRight className="w-4 h-4 ml-1" />
                                    </Link>
                                </Button>
                            </motion.div>
                        )}
                        {secondaryCta && (
                            <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }} transition={{ duration: 0.2 }}>
                                <Button
                                    asChild
                                    size="lg"
                                    variant="outline"
                                    className="border-slate-300 hover:bg-slate-50 font-semibold"
                                    data-testid="hero-secondary-cta"
                                >
                                    <Link to={secondaryCta.to}>{secondaryCta.label}</Link>
                                </Button>
                            </motion.div>
                        )}
                    </motion.div>
                )}
                {children}
            </div>
        </section>
    );
}

// Centred section heading with scroll reveal
export function SectionTitle({ eyebrow, title, subtitle, align = "left" }) {
    const isCenter = align === "center";
    return (
        <motion.div
            className={`max-w-3xl ${isCenter ? "mx-auto text-center" : ""}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: easeOutStrong }}
        >
            {eyebrow && (
                <div className="label-eyebrow mb-3">{eyebrow}</div>
            )}
            <h2 className="font-display font-black tracking-tight text-slate-950 text-3xl sm:text-4xl leading-tight">
                {title}
            </h2>
            {subtitle && (
                <p className="text-base text-slate-600 mt-4 leading-relaxed">
                    {subtitle}
                </p>
            )}
        </motion.div>
    );
}

export function CheckPill({ children }) {
    return (
        <li className="flex items-start gap-2.5 text-sm text-slate-700">
            <span className="w-5 h-5 rounded-full bg-slate-950 text-white grid place-items-center shrink-0 mt-0.5">
                <Check className="w-3 h-3" strokeWidth={3} />
            </span>
            <span className="leading-snug">{children}</span>
        </li>
    );
}
